import { FC, useEffect, useState } from "react";
import styles from "./work-cost.module.scss";
import { IWorkCost } from "./IWorkCost";
import { TableRowActions } from "../../../../ui/tableRowActions/tableRowActions";
import { useParams } from "react-router-dom";
import { useDeleteWorkCostMutation, useGetWorkCostQuery } from "./workCostApi";
import { Button } from "../../../../ui/button/Button";
import { PreloaderBar } from "../../../../ui/preloaderBar/PreloaderBar";
import { Snackbar } from "../../../../ui/snackbar/Snackbar";
import { Confirm } from "../../../../ui/confirm/confirm";
import { ReactComponent as DoneIcon } from "./assets/done.svg";
import { AddWorkCost } from "./addworkCost/addWorkCost";
import { min2HHMM } from "../../../../helpers/time";
import { EditWorkCost } from "./editWorkCost/editWorkCost";

type RowProps = Omit<IWorkCost, "flight_work_id" | "work_id" | "worker_id"> & {
  handler: (type: "edit" | "delete", id: number) => boolean;
};

const Row: FC<RowProps> = ({
  id,
  work_name,
  worker_name,
  group_1,
  group_2,
  group_3,
  group_4,
  group_5,
  group_6,
  included,
  handler,
}) => {
  return (
    <tr>
      <td>{worker_name}</td>
      <td>{work_name}</td>
      <td>{group_1 && min2HHMM(group_1)}</td>
      <td>{group_2 && min2HHMM(group_2)}</td>
      <td>{group_3 && min2HHMM(group_3)}</td>
      <td>{group_4 && min2HHMM(group_4)}</td>
      <td>{group_5 && min2HHMM(group_5)}</td>
      <td>{group_6 && min2HHMM(group_6)}</td>
      <td>{Boolean(included) && <DoneIcon />}</td>
      <td>
        <TableRowActions id={id} handler={handler} />
      </td>
    </tr>
  );
};

export const WorkCost: FC = () => {
  const { id } = useParams();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isUXOpen, setIsUXOpen] = useState(true);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [ID, setID] = useState<number | null>(null);

  const { data, error, isError, isFetching } = useGetWorkCostQuery(Number(id));

  const [
    deleteCostWork,
    {
      isError: isDeleteError,
      isLoading: isDeleteProcessing,
      isSuccess: isDeleteSuccess,
    },
  ] = useDeleteWorkCostMutation();

  const create = () => {
    setIsAddOpen(true);
  };

  const rowHandler = (type: "edit" | "delete", id: number) => {
    if (isConfirmOpen) return false;
    setIsUXOpen(true);
    setID(id);
    switch (type) {
      case "delete":
        setIsConfirmOpen(true);
        break;

      case "edit":
        setIsEditOpen(true);
        break;

      default:
        break;
    }
    return true;
  };

  useEffect(() => {
    if (!isDeleteSuccess) return;

    setIsConfirmOpen(false);
    setID(null);
  }, [isDeleteSuccess]);

  return (
    <section className={styles.main}>
      <header className={styles.header}>
        <h2>Группы оплаты</h2>
        <div className={styles.actions}>
          <Button onClick={create}>Добавить</Button>
        </div>
      </header>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>ФИО, т/н</th>
            <th>Вид работ</th>
            <th className={styles.th1}>Группа оплаты 1</th>
            <th className={styles.th1}>Группа оплаты 2</th>
            <th className={styles.th1}>Группа оплаты 3</th>
            <th className={styles.th1}>Группа оплаты 4</th>
            <th className={styles.th1}>Группа оплаты 5</th>
            <th className={styles.th1}>Группа оплаты 6</th>
            <th className={styles.th1}>Учитывать в ведомости оплаты</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data &&
            data.map((row) => (
              <Row
                key={row.id}
                id={row.id}
                worker_name={row.worker_name}
                work_name={row.work_name}
                group_1={row.group_1}
                group_2={row.group_2}
                group_3={row.group_3}
                group_4={row.group_4}
                group_5={row.group_5}
                group_6={row.group_6}
                included={row.included}
                handler={rowHandler}
              />
            ))}
        </tbody>
      </table>
      {isFetching && <PreloaderBar />}
      {isAddOpen && id && Number(id) && (
        <AddWorkCost
          close={() => setIsAddOpen(false)}
          flight_work_id={Number(id)}
        />
      )}
      { isEditOpen && ID && <EditWorkCost close={() => setIsEditOpen(false)} flight_work_id={Number(id)} work_cost_id={ID}/>}
      {isError && "status" in error && error.status !== 404 && isUXOpen && (
        <Snackbar
          className="error"
          isOpen={isError}
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка загрузки данных. Попробуйте позже
        </Snackbar>
      )}
      {isConfirmOpen && ID && (
        <Confirm
          confirm={() => deleteCostWork(ID)}
          reject={() => {
            setIsConfirmOpen(false);
            setID(null);
          }}
          text="Вы действительно хотите удалить вид работ из системы?"
          isProcessing={isDeleteProcessing}
        />
      )}
      {isDeleteSuccess && isUXOpen && (
        <Snackbar
          className="success"
          isOpen={isDeleteSuccess}
          onClose={() => setIsUXOpen(false)}
        >
          Группа оплаты удалена
        </Snackbar>
      )}
      {isDeleteError && isUXOpen && (
        <Snackbar
          className="error"
          isOpen={isDeleteError}
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка удаления. Попробуйте позже
        </Snackbar>
      )}
    </section>
  );
};
